import {
    getSyntheticUser,
    listSyntheticUsers,
} from './queries';
import {retireSyntheticUser} from './mutations';

import {GraphQLQuery} from '@aws-amplify/api';

import {
    GetSyntheticUser,
    ListSyntheticUsers,
    RetireSyntheticUser,
    SyntheticUsersFilter,
} from '../types.ts';
import {getTestingApiClient} from '../../testing-api-integration/client.ts';

// FNP-861 made this module read-only. FNP-969 adds exactly ONE write: retire.
// There is still no add, no update, and — critically — no DELETE. See mutations.ts
// for why retire tombstones rather than removes. Reuses the FNP-859 testing-api
// client; there is deliberately no second client.

// SyntheticUsersFilterInput accepts only { personaId, lifecycleStage, state }.
const normalizeFilter = (filter: SyntheticUsersFilter): SyntheticUsersFilter =>
    Object.entries(filter)
          .reduce((acc: Record<string, string>, [key, value]) => {
              if (typeof value === 'string' && value !== '') {
                  acc[key] = value;
              }
              return acc;
          }, {});

export const fetchRecords = async (
    filter: SyntheticUsersFilter = {},
    limit?: number,
    nextToken?: string | null,
) => {
    const normalized = normalizeFilter(filter);

    const response = await getTestingApiClient().graphql<GraphQLQuery<ListSyntheticUsers>>({
        query: listSyntheticUsers,
        variables: {
            ...(Object.keys(normalized).length ? {filter: normalized} : {}),
            ...(typeof limit === 'number' ? {limit} : {}),
            ...(nextToken ? {nextToken} : {}),
        },
    });

    return {
        items: response.data?.listSyntheticUsers?.items ?? [],
        nextToken: response.data?.listSyntheticUsers?.nextToken ?? null,
    };
};

export const fetch = async (id: string) => {
    const response = await getTestingApiClient().graphql<GraphQLQuery<GetSyntheticUser>>({
        query: getSyntheticUser,
        variables: {id: id},
    });
    return response.data?.getSyntheticUser;
};

// Tombstones the row — see mutations.ts. Named `retire`, never `remove` or
// `delete`, so no caller can reach for this thinking the row goes away.
export const retire = async (id: string) => {
    const response = await getTestingApiClient().graphql<GraphQLQuery<RetireSyntheticUser>>({
        query: retireSyntheticUser,
        variables: {id: id},
    });
    return response.data?.retireSyntheticUser;
};
